import Editor from './editor.js'
import { isArray, isSet, notSet } from '../helpers/utils.js'
import { getSchemaAnyOf, getSchemaOneOf, getSchemaType, getSchemaXOption } from '../helpers/schema.js'

/**
 * Represents an EditorMultiple instance.
 * @extends Editor
 */
class EditorMultiple extends Editor {
  static resolves (schema) {
    const schemaType = getSchemaType(schema)
    const schemaOneOf = getSchemaOneOf(schema)
    const schemaAnyOf = getSchemaAnyOf(schema)
    return isSet(schemaAnyOf) || isSet(schemaOneOf) || schemaType === 'any' || isArray(schemaType) || notSet(schemaType)
  }

  build () {
    this.switcherInput = getSchemaXOption(this.instance.schema, 'switcherInput') ?? this.instance.jedison.getOption('switcherInput')
    this.embedSwitcher = this.instance.jedison.getOption('embedSwitcher')
    this.control = this.theme.getMultipleControl({
      titleHidden: getSchemaXOption(this.instance.schema, 'titleHidden'),
      id: this.getIdFromPath(this.instance.path),
      switcherOptionValues: this.instance.switcherOptionValues,
      switcherOptionsLabels: this.instance.switcherOptionsLabels,
      switcher: this.switcherInput,
      readOnly: this.instance.isReadOnly()
    })

    if (this.embedSwitcher) {
      this.control.header.style.display = 'none'
    }

    // Owns exactly one wrapper for its own switcher inside a (possibly shared)
    // switcherSlot, so a nested Multiple embedding into the same slot doesn't
    // wipe this one out — see refreshUI().
    this.switcherWrapper = this.theme.getSwitcherOwner()

    this.instance.on('change', (initiator) => {
      if (initiator === 'api') return
      const jedison = this.instance.jedison
      const errors = jedison.getErrors(['error', 'warning'])
      const prefix = this.instance.path + '/'
      const matching = []

      for (const inst of jedison.instances.values()) {
        if (inst.ui && inst.path.startsWith(prefix)) {
          matching.push(inst)
        }
      }

      // Iterate in reverse (deepest/last-registered first) so leaf error states
      // are updated before descendant nav editors rebuild their tab badges
      for (const inst of matching.reverse()) {
        inst.ui.showValidationErrors(errors)
      }
    })
  }

  adaptForTable (td) {
    this.theme.adaptForTableMultipleControl(this.control, td)
  }

  adaptForHorizontal (labelCol, inputCol) {
    this.theme.adaptForHorizontalMultipleControl(this.control, labelCol, inputCol, this.getTitle())
  }

  addEventListeners () {
    if (this.switcherInput === 'select') {
      this.control.switcher.input.addEventListener('change', () => {
        const index = Number(this.control.switcher.input.value)
        this.instance.switchInstance(index, undefined, 'user')
      })
    }

    if (this.switcherInput === 'radios' || this.switcherInput === 'radios-inline') {
      this.control.switcher.radios.forEach((radio) => {
        radio.addEventListener('change', () => {
          const index = Number(radio.value)
          this.instance.switchInstance(index, undefined, 'user')
        })
      })
    }

    if (this.switcherInput === 'modal') {
      this.control.switcher.optionButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          const index = Number(btn.dataset.switcherValue)
          this.control.switcher.dialog.close()
          this.instance.switchInstance(index, undefined, 'user')
        })
      })
    }

    if (this.switcherInput === 'select-inline') {
      this.control.switcher.input.addEventListener('change', () => {
        const index = Number(this.control.switcher.input.value)
        this.instance.switchInstance(index, undefined, 'user')
      })
    }
  }

  refreshUI () {
    this.refreshDisabledState()

    // Only swap the mounted branch when it actually changed, so re-rendering
    // doesn't tear down the active branch's DOM mid-interaction (issue #65).
    const activeContainer = this.instance.activeInstance.ui.control.container
    if (this.control.childrenSlot.firstChild !== activeContainer) {
      this.control.childrenSlot.innerHTML = ''
      this.control.childrenSlot.appendChild(activeContainer)
    }

    // Recomputed on every refresh (not just once at build), since the active
    // branch — and therefore which real leaf control this resolves to — can
    // change: this is what an ancestor Multiple/IfThenElse reads to embed its
    // own switcher, drilling transparently through this node.
    const slot = this.instance.activeInstance.ui.control.switcherSlot
    this.control.switcherSlot = slot

    if (this.embedSwitcher || this.switcherInput === 'modal' || this.switcherInput === 'select-inline') {
      if (slot) {
        this.switcherWrapper.innerHTML = ''
        this.switcherWrapper.appendChild(this.control.switcher.container)
        this.insertBySwitcherDepth(slot, this.switcherWrapper, this.getNestedSwitcherDepth(this.instance.activeInstance))
        this.control.header.style.display = 'none'
      } else {
        this.control.header.style.display = ''
        this.control.header.appendChild(this.control.switcher.container)
      }
    }

    if (this.switcherInput === 'select') {
      this.control.switcher.input.value = this.instance.index
    }

    if (this.switcherInput === 'select-inline') {
      this.control.switcher.input.value = this.instance.index
    }

    if (this.switcherInput === 'radios' || this.switcherInput === 'radios-inline') {
      this.control.switcher.radios.forEach((radio) => {
        const radioIndex = Number(radio.value)
        radio.checked = radioIndex === this.instance.index
      })
    }

    if (this.switcherInput === 'modal') {
      this.control.switcher.triggerText.textContent = this.instance.switcherOptionsLabels[this.instance.index]
      this.control.switcher.optionButtons.forEach((btn, index) => {
        this.theme.setSwitcherOptionActive(btn, index === this.instance.index)
      })
    }

    if (this.disabled || this.instance.isReadOnly()) {
      this.instance.activeInstance.ui.disable()
    } else {
      this.instance.activeInstance.ui.enable()
    }
  }

  getErrorFeedback (config) {
    return this.theme.getAlert(config)
  }

  // Counts how many EditorMultiple owners sit between `instance` and the real
  // leaf control, so stacked switchers in a shared slot can be ordered
  // outer-first instead of shuffling with every refresh.
  getNestedSwitcherDepth (instance) {
    if (!instance || !instance.activeInstance) return 0
    const childDepth = this.getNestedSwitcherDepth(instance.activeInstance)
    return instance.ui.switcherWrapper ? childDepth + 1 : childDepth
  }

  insertBySwitcherDepth (slot, wrapper, depth) {
    wrapper.dataset.switcherDepth = depth
    const sibling = Array.from(slot.children).find((child) => Number(child.dataset.switcherDepth) < depth)
    if (sibling) {
      slot.insertBefore(wrapper, sibling)
    } else {
      slot.appendChild(wrapper)
    }
  }
}

export default EditorMultiple
